<template>
  <div class="card main-chart p-3" :class="{ 'main-chart--embedded': injectedReportData }" :style="!injectedReportData ? 'min-height: 560px;' : ''">
    <!-- Controls -->
    <div v-if="!injectedReportData" class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
      <div class="d-flex align-items-center gap-2">
        <DateRange compact />
      </div>
      <div class="d-flex align-items-center gap-2">
        <span class="paths-ctrl-label">{{ $t('ReportBuilder.paths.minCount') }}</span>
        <el-input-number v-model="minCount" :min="1" :max="maxEdgeCount || 1" size="small" style="width: 120px;" />
      </div>
    </div>
    <HardcodedDateWarning v-if="!injectedReportData" />

    <div class="paths-hint mb-2">{{ $t('ReportBuilder.paths.metricHint') }}</div>

    <div v-if="!loading && !hasData" class="paths-empty-state">
      <i class="fas fa-project-diagram paths-empty-icon"></i>
      <div>{{ $t('ReportBuilder.paths.empty') }}</div>
    </div>

    <div v-else-if="hasData" ref="scroll" class="paths-scroll">
      <div v-if="!layout.links.length" class="paths-empty-state">
        <div>{{ $t('ReportBuilder.paths.emptyThreshold') }}</div>
      </div>
      <svg
          v-else
          :viewBox="`0 0 ${layout.width} ${layout.height}`"
          :width="layout.width"
          :height="layout.height"
          class="paths-svg"
          @mouseleave="hideTip"
      >
        <!-- links -->
        <path
            v-for="(lk, i) in layout.links"
            :key="'l' + i"
            :d="lk.d"
            class="paths-link"
            :class="{ 'paths-link--dim': hoverKey !== null && !lk.active, 'paths-link--hot': lk.active }"
            :stroke-width="lk.width"
            @mousemove="showLinkTip($event, lk)"
            @mouseenter="hoverKey = lk.key"
            @mouseleave="hoverKey = null"
        />

        <!-- nodes + labels -->
        <g v-for="(nd, i) in layout.nodes" :key="'n' + i">
          <rect
              :x="nd.x"
              :y="nd.y"
              :width="nodeWidth"
              :height="nd.h"
              rx="2"
              class="paths-node"
              :class="{ 'paths-node--hot': hoverKey === nd.key }"
              @mousemove="showNodeTip($event, nd)"
              @mouseenter="hoverKey = nd.key"
              @mouseleave="hoverKey = null"
          />
          <g class="paths-label-g" @mousemove="showNodeTip($event, nd)" @mouseenter="hoverKey = nd.key" @mouseleave="hoverKey = null">
            <rect
                :x="nd.labelX"
                :y="nd.labelY"
                :width="nd.labelW"
                height="18"
                rx="4"
                class="paths-label-bg"
            />
            <text
                :x="nd.labelX + 6"
                :y="nd.labelY + 9"
                dominant-baseline="middle"
                class="paths-label"
            >
              <tspan class="paths-label-step">{{ nd.step }}</tspan>
              <tspan dx="3">{{ nd.short }}</tspan>
              <tspan class="paths-label-count" dx="3">{{ formatCount(nd.value) }}</tspan>
            </text>
            <title>{{ nd.name }} · {{ nd.value }}</title>
          </g>
        </g>
      </svg>
    </div>

    <!-- tooltip -->
    <div v-if="tip.show" class="paths-tip" :style="{ left: tip.x + 'px', top: tip.y + 'px' }">
      <div v-html="tip.html"></div>
    </div>
  </div>
</template>

<script>
import { useEvents } from "@/composables/useEvents";
import DateRange from "@/views/report_builder/builder/_dateRange.vue";
import HardcodedDateWarning from "@/views/report_builder/builder/_hardcodedDateWarning.vue";

const NODE_W = 14;
const HEIGHT = 520;
const PAD_Y = 24;
const NODE_GAP = 12;
const LEFT_PAD = 16;
const RIGHT_PAD = 30;
const MIN_COL_GAP = 150;
const MIN_NODE_H = 3;
const CHAR_PX = 6.1;      // approx width of a label character at 11px
const LABEL_MAX_PX = 190; // hard cap on a label chip width

export default {
  name: "_paths_chart",
  components: { DateRange, HardcodedDateWarning },
  inject: {
    injectedReportData: { from: 'injectedReportData', default: null }
  },
  setup() {
    const { getEventName: resolveEventName } = useEvents();
    return { resolveEventName };
  },
  data() {
    return {
      hoverKey: null,
      nodeWidth: NODE_W,
      minCount: 1,
      containerWidth: 0,
      tip: { show: false, x: 0, y: 0, html: '' },
      resizeObs: null
    };
  },
  computed: {
    reportData() {
      return this.injectedReportData || this.$store.state.ReportFromBuilder;
    },
    loading() {
      return this.injectedReportData ? false : this.$store.state.ReportFromBuilder.loading;
    },
    chartData() {
      return this.reportData.chartData || [];
    },
    hasData() {
      return Array.isArray(this.chartData) && this.chartData.length > 0;
    },
    maxEdgeCount() {
      return this.chartData.reduce((m, e) => Math.max(m, Number(e.event_count) || 0), 0);
    },
    /**
     * Turns the flat edge list (source_event, target_event, event_count, avg_conversion_time)
     * into positioned Sankey nodes + links. Node keys are "<step>_<label>"; step drives the
     * column, label is an event id (mapped to a name) or a group alias. Columns are spread to
     * fill the container width; edges below `minCount` are dropped to declutter.
     */
    layout() {
      const edges = this.chartData
        .map(e => {
          const src = this.parseKey(e.source_event);
          const tgt = this.parseKey(e.target_event);
          if (!src || !tgt) return null;
          const count = Number(e.event_count) || 0;
          if (count < this.minCount) return null;
          return { src, tgt, count, avg: Number(e.avg_conversion_time) || 0 };
        })
        .filter(Boolean);

      if (!edges.length) return { width: 400, height: HEIGHT, nodes: [], links: [] };

      const nodes = {};
      const ensure = (p) => {
        if (!nodes[p.key]) {
          nodes[p.key] = { key: p.key, step: p.step, label: p.label, inSum: 0, outSum: 0 };
        }
        return nodes[p.key];
      };
      edges.forEach(e => {
        const s = ensure(e.src);
        const t = ensure(e.tgt);
        s.outSum += e.count;
        t.inSum += e.count;
      });
      Object.values(nodes).forEach(n => { n.value = Math.max(n.inSum, n.outSum); });

      const steps = [...new Set(Object.values(nodes).map(n => n.step))].sort((a, b) => a - b);
      const stepIndex = Object.fromEntries(steps.map((s, i) => [s, i]));
      const lastStep = steps[steps.length - 1];
      const numCols = steps.length;

      // Column spacing: spread across the container; fall back to a min gap (then scroll).
      const usable = Math.max(360, this.containerWidth || 900) - LEFT_PAD - RIGHT_PAD - NODE_W;
      const colGap = numCols > 1 ? Math.max(MIN_COL_GAP, usable / (numCols - 1)) : 0;
      const labelBudget = Math.min(LABEL_MAX_PX, Math.max(60, colGap - NODE_W - 20));
      const maxChars = Math.max(6, Math.floor((labelBudget - 34) / CHAR_PX));

      const columns = steps.map(s =>
        Object.values(nodes).filter(n => n.step === s).sort((a, b) => b.value - a.value)
      );

      const availH = HEIGHT - 2 * PAD_Y;
      let scale = Infinity;
      columns.forEach(col => {
        const sumVal = col.reduce((a, n) => a + n.value, 0) || 1;
        const gaps = (col.length - 1) * NODE_GAP;
        scale = Math.min(scale, (availH - gaps) / sumVal);
      });
      if (!isFinite(scale) || scale <= 0) scale = 1;

      columns.forEach((col, ci) => {
        col.forEach(n => { n.h = Math.max(MIN_NODE_H, n.value * scale); });
        const colH = col.reduce((a, n) => a + n.h, 0) + (col.length - 1) * NODE_GAP;
        let y = (HEIGHT - colH) / 2;
        const x = LEFT_PAD + ci * colGap;
        const isLast = ci === stepIndex[lastStep] && numCols > 1;
        col.forEach(n => {
          n.x = x;
          n.y = y;
          n.outCursor = y;
          n.inCursor = y;
          n.name = this.nodeName(n.label);
          n.short = this.truncate(n.name, maxChars);
          const chipW = Math.min(labelBudget, (n.short.length + String(this.formatCount(n.value)).length + 3) * CHAR_PX + 22);
          n.labelW = chipW;
          n.labelX = isLast ? n.x - 8 - chipW : n.x + NODE_W + 8;
          n.labelY = n.y + n.h / 2 - 9;
          y += n.h + NODE_GAP;
        });
      });

      edges.forEach(e => { e.s = nodes[e.src.key]; e.t = nodes[e.tgt.key]; });
      Object.values(nodes).forEach(n => {
        n.outEdges = edges.filter(e => e.s === n).sort((a, b) => a.t.y - b.t.y);
      });

      const links = [];
      Object.values(nodes).forEach(n => {
        n.outEdges.forEach(e => {
          const w = Math.max(1, e.count * scale);
          const y0 = e.s.outCursor + w / 2;
          e.s.outCursor += w;
          const y1 = e.t.inCursor + w / 2;
          e.t.inCursor += w;
          const x0 = e.s.x + NODE_W;
          const x1 = e.t.x;
          const mx = (x0 + x1) / 2;
          const pct = e.s.outSum ? (e.count / e.s.outSum) * 100 : 0;
          links.push({
            key: e.s.key + '>' + e.t.key,
            d: `M${x0},${y0} C${mx},${y0} ${mx},${y1} ${x1},${y1}`,
            width: w, count: e.count, avg: e.avg, pct,
            srcName: e.s.name, tgtName: e.t.name, active: false
          });
        });
      });

      const contentW = LEFT_PAD + (numCols - 1) * colGap + NODE_W + RIGHT_PAD;
      const width = Math.max(contentW, this.containerWidth || contentW);
      const cleanNodes = Object.values(nodes).map(n => ({
        key: n.key, step: n.step, name: n.name, short: n.short, value: n.value,
        x: n.x, y: n.y, h: n.h, labelX: n.labelX, labelY: n.labelY, labelW: n.labelW
      }));
      return { width, height: HEIGHT, nodes: cleanNodes, links };
    }
  },
  watch: {
    hoverKey() {
      this.layout.links.forEach(lk => {
        lk.active = this.hoverKey !== null &&
          (lk.key === this.hoverKey || lk.key.startsWith(this.hoverKey + '>') || lk.key.endsWith('>' + this.hoverKey));
      });
    },
    maxEdgeCount(val) {
      if (this.minCount > val && val >= 1) this.minCount = 1;
    }
  },
  mounted() {
    this.measure();
    if (typeof ResizeObserver !== 'undefined' && this.$refs.scroll) {
      this.resizeObs = new ResizeObserver(() => this.measure());
      this.resizeObs.observe(this.$refs.scroll);
    }
    window.addEventListener('resize', this.measure);
  },
  updated() {
    // scroll container appears once data arrives — attach observer lazily
    if (!this.resizeObs && typeof ResizeObserver !== 'undefined' && this.$refs.scroll) {
      this.resizeObs = new ResizeObserver(() => this.measure());
      this.resizeObs.observe(this.$refs.scroll);
      this.measure();
    }
  },
  beforeUnmount() {
    if (this.resizeObs) this.resizeObs.disconnect();
    window.removeEventListener('resize', this.measure);
  },
  methods: {
    measure() {
      const el = this.$refs.scroll;
      if (el) this.containerWidth = el.clientWidth;
    },
    parseKey(raw) {
      if (raw === null || raw === undefined) return null;
      const s = String(raw);
      const i = s.indexOf('_');
      if (i < 0) return { key: s, step: 1, label: s };
      const step = parseInt(s.slice(0, i), 10);
      return { key: s, step: isNaN(step) ? 1 : step, label: s.slice(i + 1) };
    },
    nodeName(label) {
      if (/^\d+$/.test(label)) return this.resolveEventName(Number(label));
      return label;
    },
    truncate(s, max) {
      const str = String(s);
      return str.length > max ? str.slice(0, Math.max(1, max - 1)) + '…' : str;
    },
    formatCount(v) {
      const n = Number(v) || 0;
      if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
      if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
      return String(n);
    },
    formatTime(sec) {
      const s = Math.round(Number(sec) || 0);
      if (s < 60) return s + this.$t('ReportBuilder.paths.unitSec');
      if (s < 3600) return Math.round(s / 60) + this.$t('ReportBuilder.paths.unitMin');
      if (s < 86400) return (s / 3600).toFixed(1) + this.$t('ReportBuilder.paths.unitHour');
      return (s / 86400).toFixed(1) + this.$t('ReportBuilder.paths.unitDay');
    },
    showLinkTip(ev, lk) {
      this.tip = {
        show: true,
        ...this.tipPos(ev),
        html: `<div class="paths-tip-flow">${this.esc(lk.srcName)} → ${this.esc(lk.tgtName)}</div>` +
          `<div class="paths-tip-row"><span>${this.$t('ReportBuilder.paths.tipUsers')}</span><b>${this.formatCount(lk.count)}</b></div>` +
          `<div class="paths-tip-row"><span>${this.$t('ReportBuilder.paths.tipShare')}</span><b>${lk.pct.toFixed(1)}%</b></div>` +
          `<div class="paths-tip-row"><span>${this.$t('ReportBuilder.paths.tipAvgTime')}</span><b>${this.formatTime(lk.avg)}</b></div>`
      };
    },
    showNodeTip(ev, nd) {
      this.tip = {
        show: true,
        ...this.tipPos(ev),
        html: `<div class="paths-tip-flow"><span class="paths-tip-step">${nd.step}</span> ${this.esc(nd.name)}</div>` +
          `<div class="paths-tip-row"><span>${this.$t('ReportBuilder.paths.tipUsers')}</span><b>${this.formatCount(nd.value)}</b></div>`
      };
    },
    tipPos(ev) {
      const host = this.$el.getBoundingClientRect();
      return { x: ev.clientX - host.left + 12, y: ev.clientY - host.top + 12 };
    },
    hideTip() {
      this.tip.show = false;
      this.hoverKey = null;
    },
    esc(s) {
      return String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    }
  }
};
</script>

<style scoped>
.main-chart {
  border: 1px solid var(--ui-border, #e8e8e8);
  border-radius: 12px;
  background: var(--ui-bg-card, #fff);
  position: relative;
}
.main-chart--embedded { border: none; box-shadow: none; background: transparent; }
.dark-version .main-chart { background: transparent; }

.paths-ctrl-label { font-size: 12px; color: var(--ui-text-2, #606266); }
.paths-hint { font-size: 11px; color: var(--ui-text-3, #909399); }

.paths-scroll { overflow-x: auto; overflow-y: hidden; width: 100%; }
.paths-svg { display: block; }

.paths-link {
  fill: none;
  stroke: var(--el-color-primary, #409eff);
  stroke-opacity: 0.22;
  transition: stroke-opacity 0.15s ease;
  cursor: pointer;
}
.paths-link--hot { stroke-opacity: 0.55; }
.paths-link--dim { stroke-opacity: 0.06; }

.paths-node { fill: var(--el-color-primary, #409eff); cursor: pointer; }
.paths-node--hot { fill: var(--el-color-primary-dark-2, #337ecc); }

.paths-label-g { cursor: default; }
.paths-label-bg {
  fill: var(--ui-bg-card, #fff);
  fill-opacity: 0.82;
  stroke: var(--ui-border, #ebeef5);
  stroke-width: 1;
}
.paths-label {
  font-size: 11px;
  fill: var(--ui-text-1, #303133);
}
.paths-label-step { font-weight: 700; fill: var(--ui-text-3, #909399); font-size: 9px; }
.paths-label-count { fill: var(--ui-text-3, #909399); font-size: 10px; }

.paths-empty-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 260px; color: var(--ui-text-3, #909399); gap: 10px;
}
.paths-empty-icon { font-size: 32px; opacity: 0.5; }

.paths-tip {
  position: absolute;
  z-index: 20;
  pointer-events: none;
  background: var(--ui-bg-card, #fff);
  border: 1px solid var(--ui-border, #e4e7ed);
  border-radius: 8px;
  padding: 8px 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.14);
  font-size: 12px;
  min-width: 150px;
}
</style>

<style>
.paths-tip-flow { font-weight: 600; margin-bottom: 4px; color: var(--ui-text-1, #303133); }
.paths-tip-step {
  display: inline-block; min-width: 16px; text-align: center;
  background: var(--el-color-primary, #409eff); color: #fff; border-radius: 4px;
  font-size: 10px; padding: 0 4px; margin-right: 2px;
}
.paths-tip-row { display: flex; justify-content: space-between; gap: 16px; color: var(--ui-text-2, #606266); }
.paths-tip-row b { color: var(--ui-text-1, #303133); }
</style>
